import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { FiAlertCircle, FiMail, FiMessageSquare, FiPhone, FiSend, FiUser } from "react-icons/fi";
import { toast } from "sonner";
import { z } from "zod";
import ClientOnly from "~/components/shared-components/ClentOnly";
import { useContactData } from "~/hook/useContactData";
import { useFontStyle } from "~/hook/useFontStyle";
import { resetContactState, submitContactFormThunk } from "~/lib/store/slice/contactSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { USER_ACTIVITY_TRACKING_KEY } from "~/utils/constant";
import getEnv from "~/utils/get-env";

// Sanitization utility to remove HTML tags, scripts, and dangerous characters
const sanitizeInput = (input: string): string => {
  if (!input) return "";

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, "");

  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");

  // Remove dangerous characters and patterns
  sanitized = sanitized.replace(/javascript:/gi, "");
  sanitized = sanitized.replace(/on\w+\s*=/gi, ""); // Remove event handlers like onclick=
  sanitized = sanitized.replace(/eval\(/gi, "");
  sanitized = sanitized.replace(/expression\(/gi, "");

  // Decode HTML entities to prevent encoded script injection
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = sanitized;
  sanitized = tempDiv.textContent || tempDiv.innerText || "";

  // Additional cleanup for special characters that could be used in XSS
  sanitized = sanitized.replace(/[<>"'`]/g, "");

  return sanitized.trim();
};

// Custom Zod refinement to check for dangerous content
const plainTextOnly = (value: string) => /^[a-zA-Z0-9\s.,!?@()'"\-_:;/+&%#]*$/.test(value);

export const contactFormSchema = z
  .object({
    name: z.string().trim().min(5, "Name must be at least 5 characters").max(50, "Name must be less than 50 characters").refine(plainTextOnly, {
      message: "Only plain text allowed",
    }),

    email: z.string().trim().email("Invalid email address").max(100),

    phone: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^[0-9]{10}$/.test(val), "Phone number must be exactly 10 digits"),

    subject: z.string().trim().min(5).max(100).refine(plainTextOnly, {
      message: "Only plain text allowed",
    }),

    message: z.string().trim().min(10).max(1000).refine(plainTextOnly, {
      message: "Only plain text allowed",
    }),
  })
  .strict();

type ContactFormData = z.infer<typeof contactFormSchema>;

// Loading spinner component
const LoadingSpinner = ({ size = 20 }: { size?: number }) => (
  <div className="animate-spin rounded-full border-2 border-white border-t-transparent" style={{ width: size, height: size }} />
);

const ContactForm = () => {
  const { medium, mediumInter, normalInter } = useFontStyle();
  const contactData = useContactData();
  const dispatch = useAppDispatch();
  const { loading: isSubmitting, success: submitSuccess } = useAppSelector((state) => state.contact);
  const env = getEnv();

  const { contactForm } = contactData;

  // React Hook Form setup with Zod resolver
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  // Watch form values for character count
  const messageValue = watch("message", "");
  const subjectValue = watch("subject", "");

  const onSubmit = async (data: ContactFormData) => {
    const token = await (window as any).grecaptcha.enterprise.execute(env.GOOGLE_RECAPTCHA_SITE_KEY, { action: "CONTACT_FORM" });

    if (!token) {
      toast.error("reCAPTCHA verification failed. Please try again.");
      return;
    }

    try {
      const userTrackingData = sessionStorage.getItem(USER_ACTIVITY_TRACKING_KEY)
        ? JSON.parse(sessionStorage.getItem(USER_ACTIVITY_TRACKING_KEY) as string)
        : null;
      // Sanitize all input fields before submission
      const sanitizedData: ContactFormData = {
        name: sanitizeInput(data.name),
        email: sanitizeInput(data.email),
        phone: data.phone ? sanitizeInput(data.phone) : "",
        subject: sanitizeInput(data.subject),
        message: sanitizeInput(data.message),
      };

      // Create payload with the correct field names for API
      const payload = {
        full_name: sanitizedData.name,
        email: sanitizedData.email,
        phone_number: sanitizedData.phone || "",
        subject: sanitizedData.subject,
        message: sanitizedData.message,
        user_record: userTrackingData,
        recaptcha_token: token,
      };

      // Dispatch async thunk action
      const result = await dispatch(submitContactFormThunk(payload));
      if (submitContactFormThunk.fulfilled.match(result)) {
        reset();
        setTimeout(() => {
          dispatch(resetContactState());
        }, 3000);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      // Error is handled in the slice with toast notification
    }
  };

  return (
    <ClientOnly
      fallback={
        <div className="bg-cardBg p-6 sm:p-8 rounded-2xl border border-borderPrimary shadow-lg">
          <div className="mb-5 ">
            <h3 className={cn("h4_heading  text-textHeadingColor mb-2", medium)}>{contactForm.title}</h3>
            <p className={cn("text-textColorPrimary body_text_s", normalInter)}>{contactForm.description}</p>
          </div>

          <form className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                  <FiUser className="inline mr-2" size={16} />
                  {contactForm.fields.name.label} {contactForm.fields.name.required && "*"}
                </label>
                <input
                  type="text"
                  className={cn(
                    "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 body_text_xs",
                    "border-borderPrimary",
                  )}
                  placeholder={contactForm.fields.name.placeholder}
                />
              </div>

              <div>
                <label className={cn("block text-textHeadingColor mb-2 body_text_xs ", mediumInter)}>
                  <FiMail className="inline mr-2" size={16} />
                  {contactForm.fields.email.label} {contactForm.fields.email.required && "*"}
                </label>
                <input
                  type="email"
                  className={cn(
                    "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 body_text_xs",
                    "border-borderPrimary",
                  )}
                  placeholder={contactForm.fields.email.placeholder}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                  <FiPhone className="inline mr-2" size={16} />
                  {contactForm.fields.phone.label}
                </label>
                <input
                  type="tel"
                  maxLength={10}
                  className={cn(
                    "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 text-sm",
                    "border-borderPrimary",
                  )}
                  placeholder={contactForm.fields.phone.placeholder}
                />
              </div>

              <div>
                <label className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                  <FiMessageSquare className="inline mr-2" size={16} />
                  {contactForm.fields.subject.label} {contactForm.fields.subject.required && "*"}
                </label>
                <input
                  type="text"
                  className={cn(
                    "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 body_text_xs",
                    "border-borderPrimary",
                  )}
                  placeholder={contactForm.fields.subject.placeholder}
                />
                <span className="text-xs text-textSecondaryColor">0/100</span>
              </div>
            </div>

            <div>
              <label className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                <FiMessageSquare className="inline mr-2" size={16} />
                {contactForm.fields.message.label} {contactForm.fields.message.required && "*"}
              </label>
              <textarea
                rows={5}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 resize-vertical body_text_xs",
                  "border-borderPrimary",
                )}
                placeholder={contactForm.fields.message.placeholder}
              />
              <span className="text-xs text-textSecondaryColor">0/1000</span>
            </div>

            <button
              type="button"
              disabled
              className={cn(
                "w-full py-4 px-6 bg-textHeadingColor hover:bg-dailyDarshanChipColor text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm",
                "opacity-70 cursor-not-allowed",
                mediumInter,
              )}
            >
              <FiSend size={16} />
              <span>{contactForm.submitButton}</span>
            </button>
          </form>
        </div>
      }
    >
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-cardBg p-6 sm:p-8 rounded-2xl border border-borderPrimary shadow-lg"
      >
        <div className="mb-5 ">
          <h3 className={cn("h4_heading  text-textHeadingColor mb-2", medium)}>{contactForm.title}</h3>
          <p className={cn("text-textColorPrimary body_text_s", normalInter)}>{contactForm.description}</p>
        </div>

        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg"
          >
            <p className={cn("text-green-700 font-medium body_text_xs", normalInter)}>{contactForm.successMessage}</p>
          </motion.div>
        )}

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                <FiUser className="inline mr-2" size={16} />
                {contactForm.fields.name.label} {contactForm.fields.name.required && "*"}
              </label>
              <input
                id="name"
                type="text"
                {...register("name")}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 body_text_xs",
                  errors.name ? "border-red-500" : "border-borderPrimary",
                )}
                placeholder={contactForm.fields.name.placeholder}
              />
              {errors.name && (
                <p className="mt-1 text-red-500 text-xs flex items-center">
                  <FiAlertCircle className="mr-1" size={12} />
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="email" className={cn("block text-textHeadingColor mb-2 body_text_xs ", mediumInter)}>
                <FiMail className="inline mr-2" size={16} />
                {contactForm.fields.email.label} {contactForm.fields.email.required && "*"}
              </label>
              <input
                id="email"
                type="email"
                {...register("email")}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 body_text_xs",
                  errors.email ? "border-red-500" : "border-borderPrimary",
                )}
                placeholder={contactForm.fields.email.placeholder}
              />
              {errors.email && (
                <p className="mt-1 text-red-500 text-xs flex items-center">
                  <FiAlertCircle className="mr-1" size={12} />
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="phone" className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                <FiPhone className="inline mr-2" size={16} />
                {contactForm.fields.phone.label}
              </label>
              <input
                id="phone"
                type="tel"
                {...register("phone")}
                maxLength={10}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = target.value.replace(/[^0-9]/g, "");
                }}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 text-sm",
                  errors.phone ? "border-red-500" : "border-borderPrimary",
                )}
                placeholder={contactForm.fields.phone.placeholder}
              />
              {errors.phone && (
                <p className="mt-1 text-red-500 text-xs flex items-center">
                  <FiAlertCircle className="mr-1" size={12} />
                  {errors.phone.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="subject" className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
                <FiMessageSquare className="inline mr-2" size={16} />
                {contactForm.fields.subject.label} {contactForm.fields.subject.required && "*"}
              </label>
              <input
                id="subject"
                type="text"
                {...register("subject")}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 body_text_xs",
                  errors.subject ? "border-red-500" : "border-borderPrimary",
                )}
                placeholder={contactForm.fields.subject.placeholder}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.subject ? (
                  <p className="text-red-500 text-xs flex items-center">
                    <FiAlertCircle className="mr-1" size={12} />
                    {errors.subject.message}
                  </p>
                ) : (
                  <div />
                )}
                <span className="text-xs text-textSecondaryColor">{subjectValue.length}/100</span>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="message" className={cn("block text-textHeadingColor mb-2 body_text_xs", mediumInter)}>
              <FiMessageSquare className="inline mr-2" size={16} />
              {contactForm.fields.message.label} {contactForm.fields.message.required && "*"}
            </label>
            <textarea
              id="message"
              {...register("message")}
              rows={5}
              className={cn(
                "w-full px-4 py-3 border rounded-lg bg-secondaryBackground text-textColorPrimary focus:outline-none focus:ring-2 focus:ring-textHeadingColor focus:border-transparent transition-all duration-300 resize-vertical body_text_xs",
                errors.message ? "border-red-500" : "border-borderPrimary",
              )}
              placeholder={contactForm.fields.message.placeholder}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.message ? (
                <p className="text-red-500 text-xs flex items-center">
                  <FiAlertCircle className="mr-1" size={12} />
                  {errors.message.message}
                </p>
              ) : (
                <div />
              )}
              <span className="text-xs text-textSecondaryColor">{messageValue.length}/1000</span>
            </div>
          </div>

          <motion.button
            type="submit"
            data-sitekey={env?.GOOGLE_RECAPTCHA_SITE_KEY || ""}
            data-callback="onSubmit"
            data-action="submit"
            disabled={isSubmitting}
            whileHover={!isSubmitting ? { scale: 1.02 } : {}}
            whileTap={!isSubmitting ? { scale: 0.98 } : {}}
            className={cn(
              "w-full py-4 px-6 bg-textHeadingColor hover:bg-dailyDarshanChipColor text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm",
              isSubmitting ? "opacity-70 cursor-not-allowed" : "",
              mediumInter,
            )}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size={18} />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <FiSend size={16} />
                <span>{contactForm.submitButton}</span>
              </>
            )}
          </motion.button>
        </form>
      </motion.div>
    </ClientOnly>
  );
};

export default ContactForm;
